import { Paper, Title, Text, Badge, Group, Stack, Grid, Box } from "@mantine/core";
import { IconCircleCheck, IconAlertCircle } from "@tabler/icons-react";
import GrayScaleMap from "../../test_entry/gray_scale_map/GrayScaleMap";
import SimpleTestMap from "../../test_entry/maps/SimpleTestMap";
import ThresholdMap from "../../test_entry/maps/ThresholdMap";

interface VisualFieldTestCardProps {
  test: any;
  visualFieldTest: any;
  vfMapValues: any;
  eyeLabel?: string;
}

export function VisualFieldTestCard({
  test,
  visualFieldTest,
  vfMapValues,
  eyeLabel,
}: VisualFieldTestCardProps) {
  if (!test.hasMapData || !visualFieldTest) return null;
  const isC40 = visualFieldTest.testType === "C-40";
  const simpleMapValues = Object.values(visualFieldTest.points ?? {}) as number[];

  return (
    <Paper shadow="sm" p="xl" radius="md" withBorder>
      <Grid gutter="md">
        <Grid.Col span={5}>
          <Stack gap="md">
            <Group>
              <Title order={3}>{eyeLabel ? `${eyeLabel} — Visual Field Test` : test.title}</Title>
              {test.status === "normal" ? (
                <Badge color="green" variant="filled" leftSection={<IconCircleCheck size={14} />}>
                  Normal
                </Badge>
              ) : (
                <Badge color="orange" variant="filled" leftSection={<IconAlertCircle size={14} />}>
                  Attention
                </Badge>
              )}
            </Group>
            <Text c="dimmed" size="sm">
              {test.description}
            </Text>

            <Grid gutter="xs">
              {isC40 ? (
                <>
                  <Grid.Col span={4}>
                    <Paper withBorder p="sm" radius="md" bg="var(--oculera-surface-alt)">
                      <Text size="xs" c="dimmed">
                        Seen
                      </Text>
                      <Text size="lg" c="blue.6" fw={600}>
                        {test.pointsSeen ?? "N/A"}
                      </Text>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Paper withBorder p="sm" radius="md" bg="var(--oculera-surface-alt)">
                      <Text size="xs" c="dimmed">
                        Missed
                      </Text>
                      <Text size="lg" c="blue.6" fw={600}>
                        {test.pointsMissed ?? "N/A"}
                      </Text>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Paper withBorder p="sm" radius="md" bg="var(--oculera-surface-alt)">
                      <Text size="xs" c="dimmed">
                        Efficiency
                      </Text>
                      <Text size="lg" c="blue.6" fw={600}>
                        {test.efficiency != null ? `${test.efficiency.toFixed(0)}%` : "N/A"}
                      </Text>
                    </Paper>
                  </Grid.Col>
                </>
              ) : (
                <>
                  <Grid.Col span={4}>
                    <Paper withBorder p="sm" radius="md" bg="var(--oculera-surface-alt)">
                      <Text size="xs" c="dimmed">
                        VFI
                      </Text>
                      <Text size="lg" c="blue.6" fw={600}>
                        {test.vfi}%
                      </Text>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Paper withBorder p="sm" radius="md" bg="var(--oculera-surface-alt)">
                      <Text size="xs" c="dimmed">
                        MD
                      </Text>
                      <Text size="lg" c="blue.6" fw={600}>
                        {vfMapValues.md?.toFixed(1) ?? "N/A"} dB
                      </Text>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Paper withBorder p="sm" radius="md" bg="var(--oculera-surface-alt)">
                      <Text size="xs" c="dimmed">
                        PSD
                      </Text>
                      <Text size="lg" c="blue.6" fw={600}>
                        {vfMapValues.psd?.toFixed(1) ?? "N/A"} dB
                      </Text>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Paper withBorder p="sm" radius="md" bg="var(--oculera-surface-alt)">
                      <Text size="xs" c="dimmed">
                        GHT
                      </Text>
                      <Text size="sm" c="blue.6" fw={600}>
                        {vfMapValues.ght ?? "N/A"}
                      </Text>
                    </Paper>
                  </Grid.Col>
                </>
              )}
            </Grid>

            <Box>
              <Text size="sm" fw={600} mb="xs">
                Results
              </Text>
              <Text size="sm" c="dimmed">
                {test.details}
              </Text>
            </Box>

            <Box>
              <Text size="sm" fw={600} mb="xs">
                Recommendation
              </Text>
              <Text size="sm" c="dimmed">
                {test.recommendation}
              </Text>
            </Box>
          </Stack>
        </Grid.Col>

        {isC40 ? (
          <Grid.Col span={7}>
            <Stack gap="xs" align="center">
              <Text size="sm" fw={600} ta="center" mb="xs">
                C-40 Map
              </Text>
              <SimpleTestMap
                values={simpleMapValues}
                testType={visualFieldTest.testType}
                eye={visualFieldTest.eye}
                testBehaviour={visualFieldTest.testBehaviour}
              />
            </Stack>
          </Grid.Col>
        ) : (
          <>
            <Grid.Col span={3}>
              <Stack gap="xs" align="center">
                <Text size="sm" fw={600} ta="center" mb="xs">
                  Pattern Deviation
                </Text>
                <ThresholdMap
                  thresholds={vfMapValues.npd}
                  testType={visualFieldTest.testType}
                  eye={visualFieldTest.eye}
                  checkEmptyCells={true}
                />
              </Stack>
            </Grid.Col>

            <Grid.Col span={3}>
              <Stack gap="xs" align="center">
                <Text size="sm" fw={600} ta="center" mb="xs">
                  Grayscale Map
                </Text>
                <GrayScaleMap
                  input={visualFieldTest.points}
                  testType={visualFieldTest.testType}
                  eye={visualFieldTest.eye}
                />
              </Stack>
            </Grid.Col>
          </>
        )}
      </Grid>
    </Paper>
  );
}
